import { useState } from 'react';
import type { CampaignScheduleKind } from '@workspace/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { CampaignScheduleState } from './ContactAgentCampaignScheduleFields.utils';

export function ContactAgentCampaignScheduleFields({
  value,
  onChange,
}: {
  value: CampaignScheduleState;
  onChange: (next: CampaignScheduleState) => void;
}) {
  const [holidayDraft, setHolidayDraft] = useState('');
  const split = value.scheduleKind === 'RURAL';

  const addHoliday = () => {
    const date = holidayDraft.slice(0, 10);
    if (date.length !== 10 || value.holidays.includes(date) || value.holidays.length >= 100) {
      return;
    }
    onChange({ ...value, holidays: [...value.holidays, date].sort() });
    setHolidayDraft('');
  };

  const setKind = (scheduleKind: CampaignScheduleKind) => {
    if (scheduleKind === 'RURAL' && value.scheduleKind !== 'RURAL') {
      onChange({
        ...value,
        scheduleKind,
        weekdayTo: value.weekdayTo > '12:00' ? '12:00' : value.weekdayTo,
      });
      return;
    }
    onChange({ ...value, scheduleKind });
  };

  return (
    <div className='md:col-span-2 space-y-4 rounded-xl border p-4'>
      <p className='text-sm font-medium'>Horarios de contacto</p>
      <fieldset className='flex flex-wrap gap-4'>
        <legend className='sr-only'>Tipo de horario</legend>
        <KindOption
          id='schedule-urbano'
          checked={value.scheduleKind === 'URBANO'}
          label='Urbano'
          hint='Horario corrido'
          onSelect={() => setKind('URBANO')}
        />
        <KindOption
          id='schedule-rural'
          checked={value.scheduleKind === 'RURAL'}
          label='Rural'
          hint='Manana y tarde, con siesta'
          onSelect={() => setKind('RURAL')}
        />
      </fieldset>

      <DayWindow
        id='weekday'
        label='Lunes a viernes'
        split={split}
        enabled={value.weekdayEnabled}
        from={value.weekdayFrom}
        to={value.weekdayTo}
        afternoonFrom={value.weekdayAfternoonFrom}
        afternoonTo={value.weekdayAfternoonTo}
        onEnabled={weekdayEnabled => onChange({ ...value, weekdayEnabled })}
        onFrom={weekdayFrom => onChange({ ...value, weekdayFrom })}
        onTo={weekdayTo => onChange({ ...value, weekdayTo })}
        onAfternoonFrom={weekdayAfternoonFrom => onChange({ ...value, weekdayAfternoonFrom })}
        onAfternoonTo={weekdayAfternoonTo => onChange({ ...value, weekdayAfternoonTo })}
      />
      <DayWindow
        id='saturday'
        label='Sabado'
        split={split}
        enabled={value.saturdayEnabled}
        from={value.saturdayFrom}
        to={value.saturdayTo}
        afternoonFrom={value.saturdayAfternoonFrom}
        afternoonTo={value.saturdayAfternoonTo}
        onEnabled={saturdayEnabled => onChange({ ...value, saturdayEnabled })}
        onFrom={saturdayFrom => onChange({ ...value, saturdayFrom })}
        onTo={saturdayTo => onChange({ ...value, saturdayTo })}
        onAfternoonFrom={saturdayAfternoonFrom => onChange({ ...value, saturdayAfternoonFrom })}
        onAfternoonTo={saturdayAfternoonTo => onChange({ ...value, saturdayAfternoonTo })}
      />
      <DayWindow
        id='sunday'
        label='Domingo'
        split={split}
        enabled={value.sundayEnabled}
        from={value.sundayFrom}
        to={value.sundayTo}
        afternoonFrom={value.sundayAfternoonFrom}
        afternoonTo={value.sundayAfternoonTo}
        onEnabled={sundayEnabled => onChange({ ...value, sundayEnabled })}
        onFrom={sundayFrom => onChange({ ...value, sundayFrom })}
        onTo={sundayTo => onChange({ ...value, sundayTo })}
        onAfternoonFrom={sundayAfternoonFrom => onChange({ ...value, sundayAfternoonFrom })}
        onAfternoonTo={sundayAfternoonTo => onChange({ ...value, sundayAfternoonTo })}
      />

      <div className='space-y-2'>
        <Label htmlFor='holiday-date'>Excepciones por feriados</Label>
        <div className='flex flex-wrap items-end gap-2'>
          <Input
            id='holiday-date'
            type='date'
            className='max-w-xs'
            value={holidayDraft}
            onChange={event => setHolidayDraft(event.target.value.slice(0, 10))}
          />
          <Button type='button' variant='outline' size='sm' onClick={addHoliday}>
            Agregar
          </Button>
        </div>
        {value.holidays.length === 0 ? (
          <p className='text-xs text-muted-foreground'>En feriados no se contacta, aunque coincidan con un dia habil.</p>
        ) : (
          <ul className='flex flex-wrap gap-2'>
            {value.holidays.map(date => (
              <li key={date}>
                <button
                  type='button'
                  className='rounded-full border px-3 py-1 text-sm'
                  onClick={() =>
                    onChange({ ...value, holidays: value.holidays.filter(item => item !== date) })
                  }
                >
                  {date} ×
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function KindOption({
  id,
  checked,
  label,
  hint,
  onSelect,
}: {
  id: string;
  checked: boolean;
  label: string;
  hint: string;
  onSelect: () => void;
}) {
  return (
    <label htmlFor={id} className='flex cursor-pointer items-start gap-2 rounded-xl border px-3 py-2'>
      <input id={id} type='radio' name='schedule-kind' checked={checked} onChange={onSelect} />
      <span>
        <span className='block text-sm font-medium'>{label}</span>
        <span className='block text-xs text-muted-foreground'>{hint}</span>
      </span>
    </label>
  );
}

function DayWindow({
  id,
  label,
  split,
  enabled,
  from,
  to,
  afternoonFrom,
  afternoonTo,
  onEnabled,
  onFrom,
  onTo,
  onAfternoonFrom,
  onAfternoonTo,
}: {
  id: string;
  label: string;
  split: boolean;
  enabled: boolean;
  from: string;
  to: string;
  afternoonFrom: string;
  afternoonTo: string;
  onEnabled: (value: boolean) => void;
  onFrom: (value: string) => void;
  onTo: (value: string) => void;
  onAfternoonFrom: (value: string) => void;
  onAfternoonTo: (value: string) => void;
}) {
  return (
    <div className='flex flex-col gap-3 lg:flex-row lg:items-start'>
      <label className='flex items-center gap-2 lg:w-48 lg:pt-2'>
        <input
          id={`${id}-enabled`}
          type='checkbox'
          checked={enabled}
          onChange={event => onEnabled(event.target.checked)}
        />
        <span className='text-sm'>{label}</span>
      </label>
      <div className='flex flex-col gap-2'>
        <TimeRange
          id={`${id}-am`}
          label={split ? 'Manana' : undefined}
          from={from}
          to={to}
          disabled={!enabled}
          onFrom={onFrom}
          onTo={onTo}
        />
        {split ? (
          <TimeRange
            id={`${id}-pm`}
            label='Tarde'
            from={afternoonFrom}
            to={afternoonTo}
            disabled={!enabled}
            onFrom={onAfternoonFrom}
            onTo={onAfternoonTo}
          />
        ) : null}
      </div>
    </div>
  );
}

function TimeRange({
  id,
  label,
  from,
  to,
  disabled,
  onFrom,
  onTo,
}: {
  id: string;
  label?: string;
  from: string;
  to: string;
  disabled: boolean;
  onFrom: (value: string) => void;
  onTo: (value: string) => void;
}) {
  return (
    <div className='flex flex-wrap items-center gap-2'>
      {label ? <span className='w-16 text-xs text-muted-foreground'>{label}</span> : null}
      <Input id={`${id}-from`} type='time' value={from} disabled={disabled} onChange={event => onFrom(event.target.value)} />
      <span className='text-sm text-muted-foreground'>a</span>
      <Input id={`${id}-to`} type='time' value={to} disabled={disabled} onChange={event => onTo(event.target.value)} />
    </div>
  );
}
